import { formatStoryData, getReelsSizeConfig } from "./utils";
import {
  useStoryNavigation,
  withStoryNavigation,
} from "./utils/reel-navigation-context";

import { ChevronNavigationControls } from "./reel-navigation-controls/reel-navigation-controls";
import ImageRenderer from "./renderers/image-renderer/image-renderer";
import { Modal } from "@lululemon/ui-library";
import PropTypes from "prop-types";
import React from "react";
import ReelEntryPoint from "./reel-entry-point/reel-entry-point";
import Stories from "react-insta-stories";
import VideoRenderer from "./renderers/video-renderer/video-renderer";
import { compose } from "@lululemon/mwa-utils";
import styles from "./reels.module.scss";
import { useDeviceType } from "@lululemon/ecom-site/src/util/with-device-characteristics";
import { ErrorBoundary } from "@lululemon/mwa-logger";
import { productEdHandleError } from "../../util/product-education-logger";
import {
  featureReelSlidesPropType,
  entryPointVideoPropType,
} from "./reelsPropType";

const progressBarStyles = {
  mobile: {
    marginTop: "16px",
    width: "calc(100% - 20px)",
    padding: 0,
    paddingTop: 0,
  },
  tabletAndAbove: {
    marginTop: "20px",
    width: "calc(100% - 40px)",
    padding: 0,
    paddingTop: 0,
  },
  emptyBars: {
    backgroundColor: "#555555",
  },
  fullBars: {
    backgroundColor: "#ffffff",
  },
};

function Reel({
  entryPointDescription,
  entryPointTitle,
  entryPointVideo,
  header,
  isReelForCarouselList,
  featureReelSlides,
  watchAgainText,
}) {
  const { deviceType } = useDeviceType();
  const [showReel, setShowReel] = React.useState(false);
  const formattedStories = formatStoryData({
    stories: featureReelSlides,
    watchAgainText,
  });
  const storiesLength = formattedStories?.length;
  const {
    hasPresentedAllStories,
    onStoryIndexUpdateBy,
    resetStoryIndex,
    setHasPresentedAllStories,
    storyIndex,
  } = useStoryNavigation(storiesLength);

  const reelSizeConfig = React.useMemo(
    () => getReelsSizeConfig(deviceType),
    [deviceType]
  );

  React.useEffect(() => {
    if (!showReel) {
      resetStoryIndex();
      setHasPresentedAllStories(false);
    }
  }, [resetStoryIndex, setHasPresentedAllStories, showReel]);

  const handleRequestClose = () => setShowReel(false);

  const handleAllStoriesEnd = () => setHasPresentedAllStories(true);

  return (
    <ErrorBoundary
      onError={(error) => productEdHandleError(error, "Reel", "Feature Reel")}
    >
      <ReelEntryPoint
        description={entryPointDescription}
        isTabletOrBelow={!!reelSizeConfig.isMobile || !!reelSizeConfig.isTablet}
        handleClickEntry={() => setShowReel(true)}
        isReelForCarouselList={isReelForCarouselList}
        poster={entryPointVideo?.fields?.videoPreviewImageUrl}
        storiesLength={storiesLength}
        title={entryPointTitle}
        url={entryPointVideo?.fields?.videoUrl}
      />
      <Modal
        classes={{
          container: styles.modalContainer,
        }}
        className={styles.modal}
        data-testid="reel-modal"
        fullScreen
        messages={{ close: "Close" }}
        onRequestClose={handleRequestClose}
        visible={showReel}
        voiceOver={{ title: header }}
      >
        <div aria-label="Feature Reels carousel" role="region">
          <span className="sr-only" data-testid="carousel-description">
            {`Carousel with ${storiesLength} stories, one slide shown at a time.
            Use previous or next button to navigate. Video slides have controls
            for pause/play video, mute/unmute sound, and control closed
            captions. Some slides may have a call-to-action link`}
          </span>

          <div className={styles.modalContent}>
            <ChevronNavigationControls
              handleButtonContainerClick={handleRequestClose}
              isVisible={!deviceType.isMobile}
              storiesLength={storiesLength}
            >
              <Stories
                currentIndex={storyIndex}
                defaultInterval={7000}
                header={header}
                height={reelSizeConfig.height}
                onAllStoriesEnd={handleAllStoriesEnd}
                onStoryEnd={onStoryIndexUpdateBy(1)}
                preventDefault
                progressWrapperStyles={
                  hasPresentedAllStories
                    ? progressBarStyles.fullBars
                    : progressBarStyles.emptyBars
                }
                progressContainerStyles={
                  deviceType.isMobile
                    ? progressBarStyles.mobile
                    : progressBarStyles.tabletAndAbove
                }
                renderers={[ImageRenderer, VideoRenderer]}
                stories={formattedStories}
                storyContainerStyles={{
                  borderRadius: "8px",
                  backgroundColor: "#000",
                }}
                width={reelSizeConfig.width}
              />
            </ChevronNavigationControls>
          </div>
        </div>
      </Modal>
    </ErrorBoundary>
  );
}

Reel.defaultProps = {
  isReelForCarouselList: false,
};

Reel.propTypes = {
  entryPointDescription: PropTypes.string.isRequired,
  entryPointTitle: PropTypes.string.isRequired,
  entryPointVideo: entryPointVideoPropType,
  header: PropTypes.string.isRequired,
  isReelForCarouselList: PropTypes.bool,
  featureReelSlides: featureReelSlidesPropType,
  watchAgainText: PropTypes.string.isRequired,
};

export default compose(withStoryNavigation)(Reel);
